import { FormEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Check, FileDown, FileText, ThumbsUp, ThumbsDown, RefreshCcw } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileWord } from "@fortawesome/free-solid-svg-icons";
import { faMarkdown } from "@fortawesome/free-brands-svg-icons";

import { isEmpty } from "lodash-es";
import psl from "psl";
import supersub from "remark-supersub";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import rehypeKatex from "rehype-katex";
import ChartRenderer from "@/components/Markdown/ChartRenderer";

import { AppStateContext } from "@/state/AppStateContext";
import { AskResponse, Citation, CustomGPT, Feedback } from "@/api/models";
import { getCustomGPTByName, saveMessageFeedback } from "@/api/api";
import { selectCustomGPT, toggleHistoryPanel, useHandleTransitionEnd } from "@/utils/utils";
import { parseAnswer } from "./AnswerParser";
import { dedupeCitations } from "@/utils/citations";
import { CodeBlock } from "@/components/Markdown/CodeBlock";
import MarkdownTable from "@/components/Markdown/MarkdownTable";
import { InlineLink, isHttpUrl } from "@/components/InlineLink";
import { MemoizedReactMarkdown } from "@/components/Markdown/MemoizedReactMarkdown";
import type { Components } from "react-markdown";
import rehypeStreamWords, { type StreamCursor, type StreamStats } from "@/components/Markdown/rehypeStreamWords";
import UnhelpfulFeedbackContent from "./UnhelpfulFeedbackContent";
import ReportInappropriateFeedbackContent from "./ReportInappropriateFeedbackContent";
import MarkdownPdfClient from "./PDFDownload";
import { saveMarkdown } from "./MarkdownDownload";
import { downloadDocxFromMarkdown } from "./DOCXDownload";
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import "./Answer.css";
import globe from "@/assets/globe.svg";
import globe_white from "@/assets/globe_white.svg";

interface AnswerProps {
  answer: AskResponse;
  status: string;
  resubmitUserMessage: () => void;
  latest: boolean;
  convDisabled: boolean;
  feedbackDisabled?: boolean;
  terminateFileUpload: () => void;
}

/** 
 * Handle formatting how citation is displayed in tooltip
 *
 * @param {Citation} citation - The model's response object
 * @param {number} index - The model's response object
**/
const createCitationTooltipText = (citation: Citation, index: number) => {
  let citationText = "";

  // Document citation
  if (citation.title) citationText = `${citation.title}`;
  // URL citation
  else if (citation.type && citation.type == 'url_citation') citationText = citation.url_citation?.url || '';
  else citationText = `Citation ${index}`;

  return citationText;
}

/** 
 * Handle formatting how citation is displayed inline in response
 *
 * @param {Citation} citation - The model's response object
 * @param {number} index - The model's response object
**/
const createShortCitationText = (citation: Citation, index: number) => {
  let citationText = "";

  // Document citation
  if (citation.title) {
    let title = citation.title.substring(citation.title.lastIndexOf('/') + 1);
    let fileExtensionPosition = title.lastIndexOf('.');
    if (fileExtensionPosition > -1) citationText = `${(fileExtensionPosition > 25 ? title.substring(0, 25) + '...' : title.substring(0, fileExtensionPosition))}`;
    else citationText = `${(title.length > 25 ? title.substring(0, 25) + '...' : title)}`;
  }
  // URL citation
  else if (citation.type && citation.type == 'url_citation') {
    try {
      let host = new URL(citation.url_citation?.url || '').hostname.toLowerCase().replace(/^www\./, '');
      let result = psl.parse(host);

      if ('sld' in result && result.sld) citationText = result.subdomain ? `${result.subdomain}.${result.sld}` : result.sld;
    } catch {
      citationText = `link ${index}`;
    }
  }
  else citationText = `${index}`;

  return citationText;
}

const Answer = ({ answer, status, resubmitUserMessage, latest, convDisabled, feedbackDisabled = false, terminateFileUpload }: AnswerProps) => {
  const appStateContext = useContext(AppStateContext);
  const responseRef = useRef<HTMLDivElement>(null);
  useHandleTransitionEnd();
  const feedbackUnavailable = convDisabled || feedbackDisabled;
  const handleToggleHistoryPanel = () => toggleHistoryPanel(appStateContext);
  const handleSelectCustomGPT = (customGPT: CustomGPT) => selectCustomGPT(appStateContext, customGPT);
  const parsedAnswer = useMemo(() => parseAnswer(answer), [answer]);
  const isStreaming = status === 'Processing';
  // Sources are listed per source, not per retrieved chunk. Inline citation
  // pills deliberately keep using parsedAnswer.citations, which they index into.
  const uniqueCitations = useMemo(() => dedupeCitations(parsedAnswer.citations), [parsedAnswer.citations]);
  const isAwaitingResponse = parsedAnswer.markdownFormatText === 'Generating response...';
  const isPlaceholderText = isAwaitingResponse || parsedAnswer.markdownFormatText === 'Uploading...';
  const hasStreamedContent = !isPlaceholderText && !isEmpty(parsedAnswer.markdownFormatText);
  // Cache key has to change when streaming ends, otherwise the memo blocks the
  // re-render that drops the per-word spans.
  const markdownCacheKey = parsedAnswer.markdownFormatText.includes("```chartpayload") ? status : (isStreaming ? 'streaming' : 'settled');
  const [selectedCustomGPT, setSelectedCustomGPT] = useState<string>('');
  const [showSelectionOptions, setShowSelectionOptions] = useState<boolean>(true);
  const [messagedCopied, setMessageCopied] = useState<boolean>(false);
  const [sourcesOpen, setSourcesOpen] = useState<boolean>(false);
  // Words within this many positions of the write head stay animated. Advancing
  // the cursor straight to the total would cut each fade off after one chunk.
  const STREAM_WINDOW = 3;
  const streamCursorRef = useRef<StreamCursor>({ current: 0 });
  const streamStatsRef = useRef<StreamStats>({ total: 0 });
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [negativeFeedbackList, setNegativeFeedbackList] = useState<Feedback[]>([]);
  const [showReportInappropriateFeedback, setShowReportInappropriateFeedback] = useState<boolean>(false);
  const [isExportPopoverOpen, setIsExportPopoverOpen] = useState<boolean>(false);
  const [isHelpful, setIsHelpful] = useState<string>('');
  const [otherFeedbackText, setOtherFeedbackText] = useState<string>('');

  /** 
   * Intercept copy event to handle copying only selected text
  **/
  useEffect(() => {
    findLatestSelection();

    const handleCopy = (e: ClipboardEvent) => {
      const selection = window.getSelection();
      if (!selection || !selection.rangeCount) return;

      // Prevent default copy behavior
      e.preventDefault();

      // Clone selected contents
      const container = document.createElement("div");
      for (let i = 0; i < selection.rangeCount; i++) {
        const range = selection.getRangeAt(i);
        container.appendChild(range.cloneContents());
      }
      const htmlContent = container.innerHTML;
      const plainText = selection.toString();

      copyToClipboard(htmlContent, plainText);
    };

    const currentRef = responseRef.current;
    if (currentRef) currentRef.addEventListener("copy", handleCopy);
    return () => { if (currentRef) currentRef.removeEventListener("copy", handleCopy) };
  }, []);

  /** 
   * Initializes the feedback value for a given answer
   *
   * @param {AskResponse} answer - The model's response object
  **/
  const initializeAnswerFeedback = (answer: AskResponse) => {
    if (answer.message_id == undefined) return undefined;
    if (answer.feedback == undefined) return undefined;
    if (answer.feedback.split(",").length > 1) return Feedback.Negative;
    if (answer.feedback.split("other-").length > 1) return Feedback.Negative;
    if (Object.values(Feedback).includes(answer.feedback)) return answer.feedback;
    return Feedback.Neutral;
  }
  const [feedbackState, setFeedbackState] = useState(initializeAnswerFeedback(answer));

  /** 
   * Handle setting feedback state on changes
  **/
  useEffect(() => {
    if (answer.message_id == undefined) return;

    let currentFeedbackState;
    if (appStateContext?.state.feedbackState && appStateContext?.state.feedbackState[answer.message_id]) currentFeedbackState = appStateContext?.state.feedbackState[answer.message_id];
    else currentFeedbackState = initializeAnswerFeedback(answer);
    setFeedbackState(currentFeedbackState)
  }, [appStateContext?.state.feedbackState, feedbackState, answer.message_id]);

  /** 
   * Handle copying entire message content on button click
  **/
  const copyOnClick = () => {
    if (!responseRef.current) return;

    // Clone the rendered response so we don't touch the live DOM
    const clone = responseRef.current.cloneNode(true) as HTMLElement;

    // Keep presentation-only UI (citations and custom GPT follow-up sections)
    // out of the copied answer.
    clone.querySelectorAll<HTMLElement>('[data-copy-ignore="true"]').forEach(element => element.remove());

    // For every KaTeX block, pull out the raw TeX (from the <annotation>) or fallback to its innerText, then replace the entire .katex span with a plain text node.
    clone.querySelectorAll<HTMLElement>('.katex').forEach(katexEl => {
      // KaTeX embeds the original source in a <annotation>…
      const annotation = katexEl.querySelector('annotation');
      const tex = annotation?.textContent?.trim() ?? katexEl.innerText.trim();

      // Wrap back in $…$ (or \\[…\\] if you prefer display‐math) so you still see it as TeX when pasting
      const textNode = document.createTextNode(`$${tex}$`);
      katexEl.replaceWith(textNode);
    });

    // Grab your cleaned HTML and plain‐text
    const cleanedHtml = clone.innerHTML;
    const cleanedText = clone.innerText.replace(/\n{3,}/g, '\n\n').trim();

    // Copy up
    copyToClipboard(cleanedHtml, cleanedText);

    // Fire your UX bits
    setMessageCopied(true);
    setTimeout(() => setMessageCopied(false), 2000);
  };

  /** 
   * Helper function to write to the clipboard
  **/
  const copyToClipboard = (htmlContent: string, plainText: string) => {
    if (navigator.clipboard) {
      navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([htmlContent], { type: "text/html" }),
          "text/plain": new Blob([plainText], { type: "text/plain" })
        })
      ])
        .then(() => console.log("Copied successfully!"))
        .catch((err) => console.error("Copy failed: ", err));
    }
  };

  /** 
   * Handle getting SAS URL for selected file and displaying in citation panel
   *
   * @param {Citation} citation - The model's response object
  **/
  const onShowCitation = async (citation: Citation) => {
    // Document citation
    if (citation.title) {
      appStateContext?.dispatch({ type: 'SET_SELECTED_CITATIONS', payload: [citation] });
      if (screen.width <= 700 && appStateContext?.state.sidepanelOpen) handleToggleHistoryPanel();
    }
    // URL citation
    else if (citation.type && citation.type == 'url_citation') window.open(citation.url_citation?.url, '_blank');
  }

  /* React tears a component down and rebuilds it when its element *type* changes
     identity, and react-markdown uses the entries of `components` as those types.
     Declaring them inline handed ReactMarkdown a fresh function on every chunk,
     so citation pills, code blocks and tables were remounted per chunk -- which
     re-fired the pill's pop-in animation and made it flicker for the whole
     stream. The object is built once; everything it needs that changes between
     renders is read through this ref. */
  const renderContextRef = useRef({ citations: parsedAnswer.citations, status, theme: appStateContext?.state.theme, onShowCitation });
  renderContextRef.current = { citations: parsedAnswer.citations, status, theme: appStateContext?.state.theme, onShowCitation };

  const markdownComponents = useMemo<Components>(() => ({
    code({ node, className, children, ...props }) {
      const lang = (className || "").replace("language-", "").trim();
      const match = /language-(\w+)/.exec(className || 'plaintext');
      const raw = String(children || "");

      // Chart
      if (lang === "chartpayload") {
        if (renderContextRef.current.status === 'Processing') return null;
        try {
          const payload = JSON.parse(raw);
          const { spec, data } = payload || {};

          return (
            <div>
              <ChartRenderer spec={spec} data={data} />
              <div className="w-[75vw]"></div>
            </div>
          );
        } catch {
          return null;
        }
      }
      // Code block
      else if (match) {
        return <CodeBlock
          key={`code-${node?.position?.start?.offset ?? String(children).slice(0, 64)}`}
          language={(match && match[1]) || ''}
          value={String(children).replace(/\n$/, '')}
          {...props}
        />;
      }

      return <code className={`${className} inline-code text-[#424242] dark:text-[#e5e5e5]`} {...props}>{children}</code>;
    },
    table: ({ children }) => <MarkdownTable>{children}</MarkdownTable>,
    a: ({ href, children }) => {
      if (href && href.startsWith('https://custom-sms-gpt-citation/')) {
        const { citations, theme, onShowCitation: showCitation } = renderContextRef.current;
        const idx = parseInt(href.replace('https://custom-sms-gpt-citation/', ''));
        const citation = citations[idx - 1];
        if (citation) {
          return (
            <span data-copy-ignore="true" className="inline-flex items-center gap-1 align-middle rounded-[5px] bg-inset ring-1 ring-line px-1.5 py-0 text-[10.5px] text-ink-2 cursor-pointer transition-colors hover:bg-hover hover:text-ink tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs mx-0.5" style={{ animation: 'pop-in 250ms cubic-bezier(0.23,1,0.32,1) both' }} data-tip={createCitationTooltipText(citation, idx)} onClick={() => showCitation(citation)}>
              {citation.url_citation ? (
                !isEmpty(citation.url_citation.favicon_url) ?
                  <img
                    key={`${citation.url_citation.favicon_url}__${theme}`}
                    src={citation.url_citation.favicon_url || ''}
                    alt="favicon"
                    className="inline-block rounded-full align-middle w-3.5 h-3.5 m-0 me-1"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = (renderContextRef.current.theme == 'dark' ? globe_white : globe);
                    }}
                  />
                  :
                  <img src={theme == 'dark' ? globe_white : globe} alt="favicon" className="inline-block rounded-full align-middle w-3.5 h-3.5 m-0 me-1" />
              ) : null}
              <span className="font-mono">{createShortCitationText(citation, idx)}</span>
            </span>
          );
        }
        return null;
      }
      // Keep citations above on their existing renderer and decorate regular web links.
      if (isHttpUrl(href)) return <InlineLink href={href}>{children}</InlineLink>;
      return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
    },
  }), []);

  /** 
   * Handle opening citations panel
  **/
  /* Advance the reveal cursor once a chunk has been committed, so the words it
     introduced become inert -- and cannot be re-animated by a later remount. */
  useEffect(() => {
    if (!isStreaming) {
      streamCursorRef.current.current = 0;
      streamStatsRef.current.total = 0;
      return;
    }
    streamCursorRef.current.current = Math.max(
      streamCursorRef.current.current,
      streamStatsRef.current.total - STREAM_WINDOW,
    );
  });

  const openSources = () => {
    appStateContext?.dispatch({ type: 'SET_SELECTED_CITATIONS', payload: parsedAnswer.citations });
    if (screen.width <= 700 && appStateContext?.state.sidepanelOpen) handleToggleHistoryPanel();
  }

  /**
   * Small avatar for a citation -- favicon for web sources, document glyph for
   * retrieved files. Used by both the sources trigger and the expanded rail.
   *
   * @param {Citation} citation - The citation to render an avatar for
   * @param {string} sizeClass - Tailwind width/height classes
  **/
  const renderCitationAvatar = (citation: Citation, sizeClass: string) => {
    if (!citation.url_citation) return <FileText className={`${sizeClass} shrink-0 text-ink-3`} />;

    const fallback = appStateContext?.state.theme == 'dark' ? globe_white : globe;

    return (
      <img
        key={`${citation.url_citation.favicon_url}__${appStateContext?.state.theme}`}
        src={!isEmpty(citation.url_citation.favicon_url) ? citation.url_citation.favicon_url || fallback : fallback}
        alt=""
        className={`${sizeClass} inline-block shrink-0 rounded-full align-middle m-0`}
        onError={(e) => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = fallback;
        }}
      />
    );
  }

  /** 
   * Handle "Thumbs Up" clicked
  **/
  const onLikeResponseClicked = () => {
    if (answer.message_id == undefined || feedbackUnavailable) return;

    let newFeedbackState = feedbackState;
    if (feedbackState == Feedback.Positive) newFeedbackState = Feedback.Neutral;
    else newFeedbackState = Feedback.Positive;
    appStateContext?.dispatch({ type: 'SET_FEEDBACK_STATE', payload: { answerId: answer.message_id, feedback: newFeedbackState } });
    setFeedbackState(newFeedbackState);

    // Update message feedback in db
    saveMessageFeedback(answer.message_id, newFeedbackState);
  }

  /** 
   * Handle "Thumbs Down" clicked
  **/
  const onDislikeResponseClicked = (e: React.MouseEvent) => {
    if (answer.message_id == undefined || feedbackUnavailable) return;

    let newFeedbackState = feedbackState;
    if (feedbackState === undefined || feedbackState === Feedback.Neutral || feedbackState === Feedback.Positive) {
      newFeedbackState = Feedback.Negative;
      setFeedbackState(newFeedbackState);
      appStateContext?.dispatch({ type: 'SET_FEEDBACK_STATE', payload: { answerId: answer.message_id, feedback: newFeedbackState } });
    }
    else {
      e.stopPropagation();
      setIsDialogOpen(false);
      newFeedbackState = Feedback.Neutral;
      setFeedbackState(newFeedbackState);
      appStateContext?.dispatch({ type: 'SET_FEEDBACK_STATE', payload: { answerId: answer.message_id, feedback: newFeedbackState } });
      saveMessageFeedback(answer.message_id, Feedback.Neutral);
    }
  }

  /** 
   * Handle updating selected negative feedback options
  **/
  const updateFeedbackList = (ev?: FormEvent<HTMLElement | HTMLInputElement>) => {
    if (answer.message_id == undefined || feedbackUnavailable) return;
    let selectedFeedback = (ev?.target as HTMLInputElement)?.id as Feedback;

    let feedbackList = negativeFeedbackList.slice();
    if ((ev?.target as HTMLInputElement).checked) feedbackList.push(selectedFeedback);
    else feedbackList = feedbackList.filter((f) => f !== selectedFeedback);

    setNegativeFeedbackList(feedbackList);
  }

  /** 
   * Handle submitted selected negative feedback options
  **/
  const onSubmitNegativeFeedback = async () => {
    if (answer.message_id == undefined || feedbackUnavailable) return;
    
    // Build the feedback string
    const feedbackItems = negativeFeedbackList.map(feedback => {
      if (feedback === Feedback.OtherUnhelpful && otherFeedbackText.trim()) {
        return `other-${otherFeedbackText.trim()}`;
      }
      return feedback;
    });
    
    saveMessageFeedback(answer.message_id, feedbackItems.join(","));
    setShowReportInappropriateFeedback(false);
    setNegativeFeedbackList([]);
    setOtherFeedbackText('');
  }

  /** 
   * Handle opening email for user to send to open help desk ticket
  **/
  const openTicket = () => {
    setIsHelpful('no2');
    const element = document.createElement("a");
    const messages = appStateContext?.state.currentChat?.messages.reduce((result, item) => {
      return (item.role !== 'tool' ? `${result}${item.role === 'assistant' ? 'response' : item.role}: ${item.content} \r\n \r\n \r\n` : `${result}`);
    }, "");
    element.href = `mailto:it-service@sms-group.com?subject=${appStateContext?.state.currentChat?.title}&body=${encodeURIComponent(`\r\n \r\nI am not able to find the correct information in ALLY [${appStateContext?.state.currentCustomGPT?.name}], below is my conversation:\r\n \r\n \r\n` + (messages || ''))}`;
    document.body.appendChild(element);
    element.click();
    element.remove();
  }

  /** 
   * Handle checking if there is a selected Custom GPT message since last user prompt
  **/
  const findLatestSelection = () => {
    let messages = appStateContext?.state.currentChat?.messages || [];
    const lastUserIndex = messages.findLastIndex(msg => msg.role === 'user');

    // No user message found
    if (lastUserIndex === -1) return null;
    
    const messagesAfterLastUser = messages.slice(lastUserIndex + 1);
    const selectionMessage = messagesAfterLastUser.find(msg => msg.role === 'selection');
    
    if (selectionMessage && !isEmpty(selectionMessage.content)) setSelectedCustomGPT(selectionMessage.content);
  }

  /** 
   * Handle setting selected Custom GPT (from Custom GPT Selector agent) as Custom GPT for conversation
  **/
  const setCustomGPT = async () => {
    setShowSelectionOptions(false);

    // Get the selected Custom GPT from state
    const res = await getCustomGPTByName(selectedCustomGPT);
    
    // Update conversation with Custom GPT
    if (res) handleSelectCustomGPT(res);
  }

  return (
    <>
      <div className="mt-2 mb-6">
        <div className="flex">
          <div className={`markdown-body prose dark:prose-invert ${(parsedAnswer.markdownFormatText === 'Uploading...') ? `w-1/2` : `max-w-[95%]`} bg-transparent text-answer dark:text-white model-response px-1`} ref={responseRef}>
            <MemoizedReactMarkdown
              cacheKey={markdownCacheKey}
              remarkPlugins={[remarkGfm, remarkMath, supersub]}
              rehypePlugins={isStreaming
                ? [rehypeRaw, rehypeSanitize, rehypeKatex, [rehypeStreamWords, { cursor: streamCursorRef.current, stats: streamStatsRef.current }]]
                : [rehypeRaw, rehypeSanitize, rehypeKatex]}
              components={markdownComponents}
            >
              {(parsedAnswer.markdownFormatText === 'Generating response...' || parsedAnswer.markdownFormatText === 'Uploading...') ? '' : parsedAnswer.markdownFormatText}
            </MemoizedReactMarkdown>

            {isAwaitingResponse ? <span className="blinking-cursor"></span> : null}
            {isStreaming && hasStreamedContent ? <span className="stream-caret"></span> : null}

            {(!convDisabled && latest && status !== 'Processing' && (appStateContext?.state.currentCustomGPT?.id == '57fc5777-792c-40c5-9bc1-fe1df4038567' || appStateContext?.state.currentCustomGPT?.id == '8814cd1e-a7e2-45ac-b8ee-321fd203a1cc') && isEmpty(isHelpful)) ?
              <div data-copy-ignore="true" className="text-sm">
                <hr />
                Was this helpful?
                <div className="flex flex-row items-center gap-4 mt-2 mb-0">
                  <button className="btn btn-sm btn-outline btn-success" onClick={() => setIsHelpful('yes')}>Yes</button>
                  <button className="btn btn-sm btn-outline btn-error" onClick={() => setIsHelpful('no')}>No</button>
                </div>
              </div>
            : null}
            {isHelpful === 'no' ? 
              <div data-copy-ignore="true" className="text-sm">
                <hr />
                Would you like to open a ticket?
                <div className="flex flex-row items-center gap-4 mt-2 mb-0">
                  <button className="btn btn-sm btn-outline btn-success" onClick={openTicket}>Yes</button>
                  <button className="btn btn-sm btn-outline btn-error" onClick={() => setIsHelpful('no2')}>No</button>
                </div>
              </div>
            : null}
          </div>
        </div>
        <div className="flex flex-row items-center gap-4 mt-3 mb-0 mx-2">
          {(status !== 'Processing' && answer.message_id !== undefined) && <>
            {messagedCopied ?
              <div className="tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs before:mt-2" data-tip="Copied">
                <Check className="text-success" size={18} />
              </div>
            :
              <div className="tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs before:mt-2" data-tip="Copy">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="icon cursor-pointer" onClick={copyOnClick}><path d="M12.668 10.667C12.668 9.95614 12.668 9.46258 12.6367 9.0791C12.6137 8.79732 12.5758 8.60761 12.5244 8.46387L12.4688 8.33399C12.3148 8.03193 12.0803 7.77885 11.793 7.60254L11.666 7.53125C11.508 7.45087 11.2963 7.39395 10.9209 7.36328C10.5374 7.33197 10.0439 7.33203 9.33301 7.33203H6.5C5.78896 7.33203 5.29563 7.33195 4.91211 7.36328C4.63016 7.38632 4.44065 7.42413 4.29688 7.47559L4.16699 7.53125C3.86488 7.68518 3.61186 7.9196 3.43555 8.20703L3.36524 8.33399C3.28478 8.49198 3.22795 8.70352 3.19727 9.0791C3.16595 9.46259 3.16504 9.95611 3.16504 10.667V13.5C3.16504 14.211 3.16593 14.7044 3.19727 15.0879C3.22797 15.4636 3.28473 15.675 3.36524 15.833L3.43555 15.959C3.61186 16.2466 3.86474 16.4807 4.16699 16.6348L4.29688 16.6914C4.44063 16.7428 4.63025 16.7797 4.91211 16.8027C5.29563 16.8341 5.78896 16.835 6.5 16.835H9.33301C10.0439 16.835 10.5374 16.8341 10.9209 16.8027C11.2965 16.772 11.508 16.7152 11.666 16.6348L11.793 16.5645C12.0804 16.3881 12.3148 16.1351 12.4688 15.833L12.5244 15.7031C12.5759 15.5594 12.6137 15.3698 12.6367 15.0879C12.6681 14.7044 12.668 14.211 12.668 13.5V10.667ZM13.998 12.665C14.4528 12.6634 14.8011 12.6602 15.0879 12.6367C15.4635 12.606 15.675 12.5492 15.833 12.4688L15.959 12.3975C16.2466 12.2211 16.4808 11.9682 16.6348 11.666L16.6914 11.5361C16.7428 11.3924 16.7797 11.2026 16.8027 10.9209C16.8341 10.5374 16.835 10.0439 16.835 9.33301V6.5C16.835 5.78896 16.8341 5.29563 16.8027 4.91211C16.7797 4.63025 16.7428 4.44063 16.6914 4.29688L16.6348 4.16699C16.4807 3.86474 16.2466 3.61186 15.959 3.43555L15.833 3.36524C15.675 3.28473 15.4636 3.22797 15.0879 3.19727C14.7044 3.16593 14.211 3.16504 13.5 3.16504H10.667C9.9561 3.16504 9.46259 3.16595 9.0791 3.19727C8.79739 3.22028 8.6076 3.2572 8.46387 3.30859L8.33399 3.36524C8.03176 3.51923 7.77886 3.75343 7.60254 4.04102L7.53125 4.16699C7.4508 4.32498 7.39397 4.53655 7.36328 4.91211C7.33985 5.19893 7.33562 5.54719 7.33399 6.00195H9.33301C10.022 6.00195 10.5791 6.00131 11.0293 6.03809C11.4873 6.07551 11.8937 6.15471 12.2705 6.34668L12.4883 6.46875C12.984 6.7728 13.3878 7.20854 13.6533 7.72949L13.7197 7.87207C13.8642 8.20859 13.9292 8.56974 13.9619 8.9707C13.9987 9.42092 13.998 9.97799 13.998 10.667V12.665ZM18.165 9.33301C18.165 10.022 18.1657 10.5791 18.1289 11.0293C18.0961 11.4302 18.0311 11.7914 17.8867 12.1279L17.8203 12.2705C17.5549 12.7914 17.1509 13.2272 16.6553 13.5313L16.4365 13.6533C16.0599 13.8452 15.6541 13.9245 15.1963 13.9619C14.8593 13.9895 14.4624 13.9935 13.9951 13.9951C13.9935 14.4624 13.9895 14.8593 13.9619 15.1963C13.9292 15.597 13.864 15.9576 13.7197 16.2939L13.6533 16.4365C13.3878 16.9576 12.9841 17.3941 12.4883 17.6982L12.2705 17.8203C11.8937 18.0123 11.4873 18.0915 11.0293 18.1289C10.5791 18.1657 10.022 18.165 9.33301 18.165H6.5C5.81091 18.165 5.25395 18.1657 4.80371 18.1289C4.40306 18.0962 4.04235 18.031 3.70606 17.8867L3.56348 17.8203C3.04244 17.5548 2.60585 17.151 2.30176 16.6553L2.17969 16.4365C1.98788 16.0599 1.90851 15.6541 1.87109 15.1963C1.83431 14.746 1.83496 14.1891 1.83496 13.5V10.667C1.83496 9.978 1.83432 9.42091 1.87109 8.9707C1.90851 8.5127 1.98772 8.10625 2.17969 7.72949L2.30176 7.51172C2.60586 7.0159 3.04236 6.6122 3.56348 6.34668L3.70606 6.28027C4.04237 6.136 4.40303 6.07083 4.80371 6.03809C5.14051 6.01057 5.53708 6.00551 6.00391 6.00391C6.00551 5.53708 6.01057 5.14051 6.03809 4.80371C6.0755 4.34588 6.15483 3.94012 6.34668 3.56348L6.46875 3.34473C6.77282 2.84912 7.20856 2.44514 7.72949 2.17969L7.87207 2.11328C8.20855 1.96886 8.56979 1.90385 8.9707 1.87109C9.42091 1.83432 9.978 1.83496 10.667 1.83496H13.5C14.1891 1.83496 14.746 1.83431 15.1963 1.87109C15.6541 1.90851 16.0599 1.98788 16.4365 2.17969L16.6553 2.30176C17.151 2.60585 17.5548 3.04244 17.8203 3.56348L17.8867 3.70606C18.031 4.04235 18.0962 4.40306 18.1289 4.80371C18.1657 5.25395 18.165 5.81091 18.165 6.5V9.33301Z"></path></svg>
              </div>
            }
            <Popover open={isExportPopoverOpen} onOpenChange={setIsExportPopoverOpen}>
              <PopoverTrigger className="tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs before:mt-2" data-tip="Export">
                <FileDown size={16} />
              </PopoverTrigger>
              <PopoverContent className="bg-secondary border-transparent rounded-3xl shadow-short w-[150px] p-3" align="start" onClick={() => setIsExportPopoverOpen(false)}>
                <MarkdownPdfClient markdown={answer.export_text || answer.answer} citations={answer.export_citations || []} fileName={`${appStateContext?.state.currentChat?.title || 'document'}.pdf`} />
                <div className="flex items-center gap-3 hover:bg-neutral rounded-lg cursor-pointer dark:text-white py-1 px-3" onClick={() => saveMarkdown(answer.export_text || answer.answer, answer.export_citations || [], `${appStateContext?.state.currentChat?.title || 'document'}.md`)}>
                  <FontAwesomeIcon className="w-[24px]" icon={faMarkdown} size="sm" />
                  <div className="select-none text-sm">Markdown</div>
                </div>
                <div className="flex items-center gap-3 hover:bg-neutral rounded-lg cursor-pointer dark:text-white py-1 px-3" onClick={() => downloadDocxFromMarkdown(answer.export_text || answer.answer, answer.export_citations || [], `${appStateContext?.state.currentChat?.title || 'document'}.docx`)}>
                  <FontAwesomeIcon className="w-[24px]" icon={faFileWord} size="sm" />
                  <div className="select-none text-sm">DOCX</div>
                </div>
              </PopoverContent>
            </Popover>
            <div className="tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs before:mt-2" data-tip="Good response">
              <button type="button" aria-label="Good response" className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-50" disabled={feedbackUnavailable} onClick={onLikeResponseClicked}>
                <ThumbsUp aria-hidden="true" className={(feedbackState === Feedback.Positive || appStateContext?.state.feedbackState[answer.message_id] === Feedback.Positive) ? `text-success` : ``} size={16} />
              </button>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger className="tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs before:mt-2" data-tip="Bad response" aria-label="Bad response" disabled={feedbackUnavailable}>
                <ThumbsDown aria-hidden="true" className={`${!feedbackUnavailable ? `cursor-pointer` : ``} ${(feedbackState !== Feedback.Positive && feedbackState !== Feedback.Neutral && feedbackState !== undefined) ? `text-error` : ``}`} size={16} onClick={(e) => !feedbackUnavailable ? onDislikeResponseClicked(e) : null} />
              </DialogTrigger>
              <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short" aria-describedby={undefined}>
                <DialogHeader className="text-sm">
                  <DialogTitle className="mb-3">Submit Feedback</DialogTitle>

                  <div>Your feedback will improve this experience.</div>

                  {!showReportInappropriateFeedback ? <UnhelpfulFeedbackContent negativeFeedbackList={negativeFeedbackList} updateFeedbackList={updateFeedbackList} setShowReportInappropriateFeedback={setShowReportInappropriateFeedback} otherFeedbackText={otherFeedbackText} setOtherFeedbackText={setOtherFeedbackText} /> : <ReportInappropriateFeedbackContent negativeFeedbackList={negativeFeedbackList} updateFeedbackList={updateFeedbackList} />}

                  <div>By pressing submit, your feedback will be visible to the application owner.</div>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild><button type="button" className="btn btn-secondary">Cancel</button></DialogClose>
                  <DialogClose asChild><button type="button" className="btn btn-primary" disabled={negativeFeedbackList.length < 1 || feedbackUnavailable} onClick={onSubmitNegativeFeedback}>Submit</button></DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>}
          {!convDisabled && latest && status !== 'Processing' ? <div className="tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs before:mt-2" data-tip="Try again"><RefreshCcw className="cursor-pointer" size={16} onClick={resubmitUserMessage} /></div> : null}
          {(status !== 'Processing' && appStateContext?.state.currentModel?.id == 'modelrouter' && !isEmpty(answer.model)) && <div className="text-sm cursor-default tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs before:mt-2" data-tip="Model">{answer.model?.replace(/-(chat|\d{4}-\d{1,2}-\d{1,2}|latest).*$/, "")}</div>}
          {(status !== 'Processing' && !isEmpty(uniqueCitations)) && (
            <button
              type="button"
              aria-expanded={sourcesOpen}
              className="-mx-1 flex items-center gap-1.5 rounded-control px-1 py-0.5 transition-colors hover:bg-hover"
              onClick={() => setSourcesOpen((open) => !open)}
            >
              <span className="flex items-center -space-x-2">
                {uniqueCitations.slice(0, 3).map((citation, i) => (
                  <span key={i} className="flex h-4 w-4 items-center justify-center rounded-full bg-inset">
                    {renderCitationAvatar(citation, 'w-2.5 h-2.5')}
                  </span>
                ))}
              </span>
              <span className="text-[12px] text-ink-2">{uniqueCitations.length} {uniqueCitations.length === 1 ? 'source' : 'sources'}</span>
            </button>
          )}
        </div>

        {(status !== 'Processing' && !isEmpty(uniqueCitations)) && (
          <div
            className="grid transition-[grid-template-rows,opacity] duration-300"
            style={{
              gridTemplateRows: sourcesOpen ? '1fr' : '0fr',
              opacity: sourcesOpen ? 1 : 0,
              transitionTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)',
            }}
          >
            <div className="overflow-hidden">
              <div className="mx-2 mt-1.5 flex flex-col rounded-[10px] bg-inset p-1">
                {uniqueCitations.map((citation, i) => (
                  <button
                    key={i}
                    type="button"
                    className="flex items-center gap-2 rounded-[6px] px-1.5 py-1 text-left text-[12px] text-ink-2 transition-colors hover:bg-hover hover:text-ink"
                    onClick={() => onShowCitation(citation)}
                  >
                    {renderCitationAvatar(citation, 'w-4 h-4')}
                    <span className="truncate">{createCitationTooltipText(citation, i + 1)}</span>
                  </button>
                ))}
                <button
                  type="button"
                  className="mt-0.5 flex items-center rounded-[6px] px-1.5 py-1 text-left text-[11.5px] text-ink-3 transition-colors hover:bg-hover hover:text-ink-2"
                  onClick={openSources}
                >
                  View all in panel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {(!convDisabled && latest && status !== 'Processing' && appStateContext?.state.selectedTool === 'cgpt_selector' && !isEmpty(selectedCustomGPT) && showSelectionOptions) ?
        <div className="my-2">
          <div className="flex">
            <div className="w-[95%] border-2 border-dashed border-secondary rounded-3xl text-answer dark:text-white model-response text-center text-sm py-3 px-4">
              Would you like to chat with {selectedCustomGPT} or continue searching for a different Custom GPT?
              <div className="flex justify-center gap-4 mt-2">
                <button type="button" className="btn btn-sm btn-outline btn-success" onClick={setCustomGPT}>Chat with {selectedCustomGPT}</button>
                <button type="button" className="btn btn-sm btn-outline btn-primary" onClick={() => setShowSelectionOptions(false)}>Continue Searching</button>
              </div>
            </div>
          </div>
        </div>
      : null}
    </>
  );
}

export default Answer;
